// =============================================================================
// Dice Expression Parser
// Handles standard tabletop notation: "2d6+15", "1d8", "3d6-2", "10"
// =============================================================================

export interface ParsedDice {
  diceCount: number;   // 0 = flat-modifier-only row
  diceSize:  number;
  modifier:  number;   // signed flat modifier
}

/**
 * Parse a dice expression string into its components.
 * Returns null if the expression is invalid / unparseable.
 *
 * Accepted formats:
 *   "2d6+15"  → { diceCount: 2, diceSize: 6,  modifier: 15 }
 *   "1d8"     → { diceCount: 1, diceSize: 8,  modifier: 0  }
 *   "3d6-2"   → { diceCount: 3, diceSize: 6,  modifier: -2 }
 *   "10"      → { diceCount: 0, diceSize: 0,  modifier: 10 }
 *   "+5"      → { diceCount: 0, diceSize: 0,  modifier: 5  }
 */
export function parseDiceExpr(expr: string): ParsedDice | null {
  const s = expr.trim().toLowerCase();
  if (!s) return null;

  // XdY, XdY+Z, XdY-Z
  const diceMatch = s.match(/^(\d+)d(\d+)([+-]\d+)?$/);
  if (diceMatch) {
    return {
      diceCount: parseInt(diceMatch[1]!),
      diceSize:  parseInt(diceMatch[2]!),
      modifier:  diceMatch[3] ? parseInt(diceMatch[3]) : 0,
    };
  }

  // Flat modifier only: "10", "+5", "-3"
  const flatMatch = s.match(/^([+-]?\d+)$/);
  if (flatMatch) {
    return { diceCount: 0, diceSize: 0, modifier: parseInt(flatMatch[1]!) };
  }

  return null;
}

/** Returns true if the expression is non-empty and parseable. */
export function isValidDiceExpr(expr: string): boolean {
  return expr.trim().length > 0 && parseDiceExpr(expr) !== null;
}

/**
 * Returns true for self-contained damage values that belong in the diceExpr field:
 *   • Pure dice notation:  "2d6", "1d8", "3d4"
 *   • Plain positive integer: "2", "10"  (for fixed-value damage like ability drain)
 *
 * Rejects embedded flat modifiers ("2d6+15") — those belong in modComponents.
 */
export function isPureDiceExpr(expr: string): boolean {
  const s = expr.trim();
  return /^\d+d\d+$/i.test(s) || /^\d+$/.test(s);
}

/**
 * Rebuild a canonical dice expression string from components.
 * e.g. (2, 6, 15) → "2d6+15",  (0, 0, 10) → "10",  (1, 8, 0) → "1d8"
 */
export function canonicalDiceExpr(diceCount: number, diceSize: number, modifier: number): string {
  if (diceCount <= 0) return String(modifier);
  const base = `${diceCount}d${diceSize}`;
  if (modifier === 0) return base;
  return `${base}${modifier > 0 ? '+' : ''}${modifier}`;
}
